import type { ExifMetadata } from '../types/metadata.js';
import type { OfficialBounds } from '../types/profile.js';

/**
 * Colour-space reporting.
 *
 * The check may only say what the *file* declares: an EXIF `ColorSpace` tag, an
 * embedded ICC profile header, or nothing at all. The absence of an ICC profile
 * is never a failure — plenty of valid camera JPEGs carry sRGB implicitly — so
 * "nothing declared" degrades to an honest `uncertain`, never to a rejection.
 */

export type ColorSpaceKind =
  | 'srgb'
  | 'rgb'
  | 'non-rgb'
  | 'uncalibrated'
  | 'other'
  | 'unknown'
  | 'none';

export interface ColorSpaceDeclaration {
  kind: ColorSpaceKind;
  /** Human label of the declared space, e.g. `sRGB`, `CMYK`. */
  label?: string;
  source: 'exif' | 'icc' | 'none';
}

/** Classifies the 4-byte colour-space signature of an ICC profile header. */
export const iccColourSpaceKind = (colourSpace: string | undefined): ColorSpaceKind => {
  if (!colourSpace) return 'unknown';
  const value = colourSpace.trim().toLowerCase();
  if (value.startsWith('rgb')) return 'rgb';
  if (value.includes('cmyk') || value.startsWith('gray') || value.startsWith('grey')) {
    return 'non-rgb';
  }
  return 'unknown';
};

/**
 * Combines the two declarations a file may carry.
 *
 * An explicit non-RGB ICC profile wins over the EXIF tag: the profile is the
 * authoritative description of the pixels' space, while `ColorSpace` is only a
 * hint. With no ICC at all, the EXIF tag alone is used.
 */
export const classifyColorSpace = (exif: ExifMetadata | undefined): ColorSpaceDeclaration => {
  const icc = exif?.iccProfile;
  if (icc?.present) {
    const kind = iccColourSpaceKind(icc.colourSpace);
    if (kind === 'rgb') return { kind: 'rgb', label: 'RGB', source: 'icc' };
    if (kind === 'non-rgb') {
      const label = (icc.colourSpace ?? '').trim().toUpperCase() || 'не RGB';
      return { kind: 'non-rgb', label, source: 'icc' };
    }
    if (exif?.colorSpace === 1) return { kind: 'srgb', label: 'sRGB', source: 'exif' };
    return { kind: 'other', label: 'ICC без указания пространства', source: 'icc' };
  }

  if (exif?.colorSpace === 1) return { kind: 'srgb', label: 'sRGB', source: 'exif' };
  if (exif?.colorSpace === 0xffff) {
    return { kind: 'uncalibrated', label: 'uncalibrated', source: 'exif' };
  }
  if (typeof exif?.colorSpace === 'number') {
    return { kind: 'other', label: `0x${exif.colorSpace.toString(16)}`, source: 'exif' };
  }
  return { kind: 'none', source: 'none' };
};

/**
 * True when the profile asks for sRGB.
 *
 * Two data-driven signals are accepted: an explicit `params.requireSrgb`, and an
 * `official` block whose statement/unit/reference mentions sRGB. Neither is
 * document-specific branching — both are values the profile supplies.
 */
export const srgbRequiredBy = (
  params: Record<string, unknown>,
  official: OfficialBounds | undefined,
): boolean => {
  if (params['requireSrgb'] === true) return true;
  const officialText = `${official?.statement ?? ''} ${official?.unit ?? ''} ${
    official?.reference ?? ''
  }`;
  return /srgb/i.test(officialText);
};

export type ColorProfileVerdict = 'pass' | 'warning' | 'uncertain';

export type ColorProfileReason =
  | 'srgb-declared'
  | 'rgb-declared'
  | 'no-requirement'
  | 'uncalibrated-required'
  | 'other-required'
  | 'non-rgb'
  | 'space-not-allowed'
  | 'unknown';

export interface ColorProfileAssessment {
  verdict: ColorProfileVerdict;
  reason: ColorProfileReason;
}

const matchesAllowed = (label: string, allowed: string[]): boolean => {
  const value = label.trim().toLowerCase();
  return allowed.some((space) => {
    const candidate = space.trim().toLowerCase();
    if (candidate.length === 0) return false;
    return value.includes(candidate) || candidate.includes(value);
  });
};

export const assessColorProfile = (input: {
  declaration: ColorSpaceDeclaration;
  srgbRequired: boolean;
  allowedSpaces: string[];
}): ColorProfileAssessment => {
  const { declaration, srgbRequired, allowedSpaces } = input;

  // Nothing was declared at all: honest uncertainty, never a failure.
  if (declaration.kind === 'none') return { verdict: 'uncertain', reason: 'unknown' };

  // A profile that states CMYK/GRAY is a real, declared mismatch with RGB work.
  if (declaration.kind === 'non-rgb') return { verdict: 'warning', reason: 'non-rgb' };

  const knownSpace = declaration.kind === 'srgb' || declaration.kind === 'rgb';
  if (knownSpace && allowedSpaces.length > 0 && declaration.label !== undefined) {
    if (!matchesAllowed(declaration.label, allowedSpaces)) {
      return { verdict: 'warning', reason: 'space-not-allowed' };
    }
  }

  if (declaration.kind === 'srgb') return { verdict: 'pass', reason: 'srgb-declared' };
  if (declaration.kind === 'rgb') return { verdict: 'pass', reason: 'rgb-declared' };

  // `uncalibrated`, a vendor EXIF value, or an ICC profile whose header does not
  // name a space: only a stated sRGB requirement makes this worth a warning.
  if (srgbRequired) {
    return {
      verdict: 'warning',
      reason: declaration.kind === 'uncalibrated' ? 'uncalibrated-required' : 'other-required',
    };
  }
  return { verdict: 'pass', reason: 'no-requirement' };
};
